import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, keyframes } from "@mui/material";
import { HOL } from "../UserSide/pages/holTheme";

const pop = keyframes`
  0% { transform: scale(0.35); opacity: 0; }
  55% { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

/** Map a server countdown payload onto this device's clock. */
export function toLocalCountdownEndsAt(payload) {
  const remaining = Number(payload?.remaining_ms);
  if (Number.isFinite(remaining) && remaining > 0) return Date.now() + remaining;
  const ends = Number(payload?.ends_at);
  if (Number.isFinite(ends) && ends > Date.now()) return ends;
  return null;
}

/**
 * One countdown per session start. A repeat of the same payload does not restart the count.
 */
export function useHolSessionCountdown() {
  const [endsAt, setEndsAt] = useState(null);
  const keyRef = useRef(null);

  const applyCountdown = useCallback((payload) => {
    if (!payload) return;
    const key = `${payload.session_id ?? ""}:${payload.ends_at ?? ""}`;
    if (!payload.ends_at || keyRef.current === key) return;
    const localEnds = toLocalCountdownEndsAt(payload);
    if (!localEnds) return;
    keyRef.current = key;
    setEndsAt(localEnds);
  }, []);

  const clearCountdown = useCallback(() => setEndsAt(null), []);

  return { countdownEndsAt: endsAt, applyCountdown, clearCountdown };
}

const digitFromEndsAt = (endsAt, now = Date.now()) => {
  const ms = endsAt - now;
  if (ms <= 0) return 0;
  return Math.min(3, Math.ceil(ms / 1000));
};

/** Full-screen 3 · 2 · 1 shared by host, players, and spectators. */
const HolSessionCountdown = ({ endsAt, onDone }) => {
  const [digit, setDigit] = useState(() => digitFromEndsAt(endsAt));
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    let finished = false;
    const tick = () => {
      const next = digitFromEndsAt(endsAt);
      setDigit(next);
      if (next <= 0 && !finished) {
        finished = true;
        onDoneRef.current?.();
      }
    };
    tick();
    const id = setInterval(tick, 80);
    return () => clearInterval(id);
  }, [endsAt]);

  if (digit <= 0 || typeof document === "undefined") return null;

  return createPortal(
    <Box
      role="status"
      aria-live="assertive"
      aria-label={`Starting in ${digit}`}
      sx={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100dvh",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        bgcolor: "#000",
      }}
    >
      <Typography
        sx={{
          color: HOL.gold,
          fontWeight: 800,
          letterSpacing: "0.28em",
          fontSize: { xs: "0.8rem", sm: "0.95rem" },
          mb: 1,
          fontFamily: HOL.fontDisplay,
        }}
      >
        GET READY
      </Typography>
      <Typography
        key={digit}
        sx={{
          fontFamily: HOL.fontDisplay,
          fontWeight: 900,
          fontSize: { xs: "8rem", sm: "11rem" },
          lineHeight: 0.9,
          background: `linear-gradient(135deg, ${HOL.gold}, ${HOL.lower})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          filter: "drop-shadow(0 0 36px rgba(245,215,110,0.4))",
          animation: `${pop} 0.45s ease-out both`,
        }}
      >
        {digit}
      </Typography>
    </Box>,
    document.body,
  );
};

export default HolSessionCountdown;
